import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiCheck, FiFolder, FiFileText, FiSend, FiChevronRight, FiSearch } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../services/api';
import { foldersService } from '../../services/folders.service';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  Section,
  SectionTitle,
  SectionDescription,
  SelectionGrid,
  SelectionCard,
  CardIcon,
  CardContent,
  CardTitle,
  CardDescription,
  CheckboxContainer,
  QuickRepliesContainer,
  QuickReply,
  ActionButtons,
  CancelButton,
  ContinueButton,
  LoadingState,
  EmptyState,
  StepHeader,
  StepItem,
  StepBullet,
  StepLabel,
  StepSubLabel,
  StepDivider,
  SearchContainer,
  SearchIcon,
  SearchInput,
  ClearSearchButton,
  SelectionHeader,
  SelectionCount,
  SelectionActions,
  HelperText,
  SummarySection,
  SummaryTitle,
  SummaryList,
  SummaryListItem,
  EmptyFilterState,
  Badge,
  ActionHint
} from './styles';
import { env } from '../../../env';

const STEP_CONFIG = [
  { id: 'prompts', label: 'Prompts', description: 'Defina o foco da análise' },
  { id: 'folders', label: 'Pastas', description: 'Escolha bases de referência' },
  { id: 'summary', label: 'Resumo', description: 'Revise antes de enviar' }
] as const;

interface Rule {
  _id: string;
  name: string;
  prompt: string;
  description?: string;
}

interface FolderData {
  id: string;
  name: string;
  description?: string;
  fileCount: number;
}

interface ChatSetupModalProps {
  isVisible: boolean;
  onClose: () => void;
  complianceType?: string;
}

const ChatSetupModal: React.FC<ChatSetupModalProps> = ({ 
  isVisible, 
  onClose, 
  complianceType 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [rules, setRules] = useState<Rule[]>([]);
  const [folders, setFolders] = useState<FolderData[]>([]);
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<'prompts' | 'folders' | 'summary'>('prompts');
  const [ruleSearchTerm, setRuleSearchTerm] = useState('');
  const [folderSearchTerm, setFolderSearchTerm] = useState('');

  useEffect(() => {
    if (isVisible) {
      loadData();
    }
  }, [isVisible]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('@App:token');
      
      if (!user || !user.organizationId) {
        throw new Error('Usuário ou organização não encontrada');
      }

      const organizationId = Array.isArray(user.organizationId) 
        ? user.organizationId[0] 
        : user.organizationId;

      // Carregar prompts/rules
      const [rulesResponse, foldersResponse] = await Promise.all([
        api.get(`/api/prompt/organization/${organizationId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        foldersService.getMyFolders()
      ]);

      setRules(rulesResponse.data || []);
      setFolders(foldersResponse.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRuleToggle = (ruleId: string) => {
    setSelectedRules(prev => 
      prev.includes(ruleId) 
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const handleFolderToggle = (folderId: string) => {
    setSelectedFolders(prev => 
      prev.includes(folderId) 
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const handleSelectAllRules = () => {
    if (selectedRules.length === rules.length) {
      setSelectedRules([]);
    } else {
      setSelectedRules(rules.map(rule => rule._id));
    }
  };

  const handleSelectAllFolders = () => {
    if (selectedFolders.length === folders.length) {
      setSelectedFolders([]);
    } else {
      setSelectedFolders(folders.map(folder => folder.id));
    }
  };

  const handleCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    callback: () => void
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const filteredRules = useMemo(() => {
    const term = ruleSearchTerm.trim().toLowerCase();
    if (!term) {
      return rules;
    }

    return rules.filter(rule =>
      rule.name.toLowerCase().includes(term) ||
      (rule.description || '').toLowerCase().includes(term)
    );
  }, [rules, ruleSearchTerm]);

  const filteredFolders = useMemo(() => {
    const term = folderSearchTerm.trim().toLowerCase();
    if (!term) {
      return folders;
    }

    return folders.filter(folder =>
      folder.name.toLowerCase().includes(term) ||
      (folder.description || '').toLowerCase().includes(term)
    );
  }, [folders, folderSearchTerm]);

  const currentStepIndex = useMemo(
    () => STEP_CONFIG.findIndex(step => step.id === currentStep),
    [currentStep]
  );

  const pluralize = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`;

  const actionHintMessage = useMemo(() => {
    if (currentStep === 'prompts') {
      return selectedRules.length > 0
        ? 'Prompts personalizados vão guiar a IA com mais precisão.'
        : 'Você pode avançar sem prompts personalizados, se preferir.';
    }

    if (currentStep === 'folders') {
      return selectedFolders.length > 0
        ? 'As pastas selecionadas serão disponibilizadas durante a análise.'
        : 'Envie pastas para contextualizar a análise ou avance sem arquivos.';
    }

    return selectedRules.length === 0 && selectedFolders.length === 0
      ? 'Nenhum prompt ou pasta selecionado: utilizaremos as instruções padrão.'
      : 'Revise os itens escolhidos antes de iniciar o chat.';
  }, [currentStep, selectedFolders.length, selectedRules.length]);

  const handleContinue = async () => {
    if (currentStep === 'prompts') {
      setCurrentStep('folders');
    } else if (currentStep === 'folders') {
      setCurrentStep('summary');
    } else {
      // Enviar para o chat - fazer chamada da API primeiro
      const selectedRulesData = rules.filter(rule => selectedRules.includes(rule._id));
      const selectedFoldersData = folders.filter(folder => selectedFolders.includes(folder.id));
      
      try {
        // Definir tipo baseado na seleção
        let complianceApiType = null;
        if (selectedFoldersData.length > 0 && selectedRulesData.length > 0) {
          complianceApiType = 'upload_doc_and_text';
        } else if (selectedFoldersData.length > 0) {
          complianceApiType = 'upload_doc';
        }
        // Se apenas prompt, não envia nada (null)

        let apiResponse = null;
        
        // Fazer chamada da API se houver tipo definido
        if (complianceApiType) {
          apiResponse = await callComplianceAPI(selectedRulesData, selectedFoldersData, complianceApiType);
        }
        
        // Navegar para o chat com os dados selecionados e resposta da API
        navigate('/compliance/pending?full=true', {
          state: {
            complianceType,
            isNewSubmission: true,
            selectedPrompts: selectedRulesData.map(rule => ({
              _id: rule._id,
              name: rule.name,
              prompt: rule.prompt,
              description: rule.description
            })),
            selectedFolders: selectedFoldersData.map(folder => ({
              id: folder.id,
              name: folder.name,
              description: folder.description,
              fileCount: folder.fileCount
            })),
            setupComplete: true,
            apiResponse: apiResponse,
            complianceApiType: complianceApiType
          }
        });
        
        onClose();
      } catch (error) {
        console.error('Erro ao chamar API de compliance:', error);
        // Mesmo com erro, continuar para o chat
        navigate('/compliance/pending?full=true', {
          state: {
            complianceType,
            isNewSubmission: true,
            selectedPrompts: selectedRulesData.map(rule => ({
              _id: rule._id,
              name: rule.name,
              prompt: rule.prompt,
              description: rule.description
            })),
            selectedFolders: selectedFoldersData.map(folder => ({
              id: folder.id,
              name: folder.name,
              description: folder.description,
              fileCount: folder.fileCount
            })),
            setupComplete: true,
                         apiError: error instanceof Error ? error.message : 'Erro na API de compliance'
          }
        });
        
        onClose();
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 'folders') {
      setCurrentStep('prompts');
    } else if (currentStep === 'summary') {
      setCurrentStep('folders');
    }
  };

  const handleSkipStep = () => {
    if (currentStep === 'prompts') {
      setSelectedRules([]);
      setCurrentStep('folders');
    } else if (currentStep === 'folders') {
      setSelectedFolders([]);
      setCurrentStep('summary');
    }
  };

  // Função auxiliar para buscar arquivos de uma pasta específica
  const fetchFolderFiles = async (folderId: string): Promise<any[]> => {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await fetch(`${env.API_URL}/api/folders/${folderId}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      return Array.isArray(data) ? data : data.data || [];
    } catch (error) {
      console.error(`Erro ao buscar arquivos da pasta ${folderId}:`, error);
      return [];
    }
  };

  // Função para processar pastas selecionadas e obter arquivos
  const processSelectedFolders = async (folders: FolderData[]): Promise<any[]> => {
    if (!folders || folders.length === 0) return [];
    
    const allFiles: any[] = [];
    
    for (const folder of folders) {
      try {
        const files = await fetchFolderFiles(folder.id);
        const filesWithFolder = files.map(file => ({
          ...file,
          folderName: folder.name,
          folderId: folder.id
        }));
        allFiles.push(...filesWithFolder);
      } catch (error) {
        console.error(`Erro ao processar pasta ${folder.name}:`, error);
      }
    }
    
    return allFiles;
  };

  // Função para chamar a API de compliance
  const callComplianceAPI = async (prompts: Rule[], folders: FolderData[], type: string) => {
    const token = localStorage.getItem('@App:token');
    
    if (!user || !user.organizationId) {
      throw new Error('Usuário ou organização não encontrada');
    }

    const organizationId = Array.isArray(user.organizationId) 
      ? user.organizationId[0] 
      : user.organizationId;

    // Preparar dados dos prompts
    const promptsData = prompts.map(prompt => ({
      id: prompt._id,
      name: prompt.name,
      content: prompt.prompt
    }));

    // Buscar arquivos das pastas
    const allFiles = await processSelectedFolders(folders);
    
    // Preparar dados dos arquivos
    const filesData = allFiles.map(file => ({
      id: file.id,
      name: file.originalName || file.name,
      type: file.type,
      size: file.size,
      folderId: file.folderId,
      folderName: file.folderName
    }));

    // Obter modelo selecionado do localStorage
    const selectedModel = localStorage.getItem('selectedGptModel') || 'gpt-4';

    const requestBody = {
      type: type,
      organizationId: organizationId,
      userId: user.id,
      model: selectedModel,
      prompts: promptsData,
      files: filesData,
      folders: folders.map(folder => ({
        id: folder.id,
        name: folder.name,
        fileCount: folder.fileCount
      }))
    };

    console.log('Enviando para API de compliance:', requestBody);

    const response = await api.post('/api/compliance', requestBody, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('Resposta da API de compliance:', response.data);
    return response.data;
  };

  if (!isVisible) return null;

  if (loading) {
    return (
      <ModalOverlay>
        <ModalContainer>
          <LoadingState>
            <div>Carregando opções...</div>
          </LoadingState>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {currentStep === 'prompts' && (complianceType === 'menu' ? 'Configurar Chat - Prompts' : 'Selecionar Prompts')}
            {currentStep === 'folders' && (complianceType === 'menu' ? 'Configurar Chat - Pastas' : 'Selecionar Pastas')}
            {currentStep === 'summary' && 'Resumo da Seleção'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            {FiX({ size: 24 })}
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <StepHeader aria-label="Progresso da configuração">
            {STEP_CONFIG.map((step, index) => {
              const isActive = currentStep === step.id;
              const isCompleted = index < currentStepIndex;

              return (
                <React.Fragment key={step.id}>
                  <StepItem active={isActive} completed={isCompleted}>
                    <StepBullet active={isActive} completed={isCompleted}>
                      {index + 1}
                    </StepBullet>
                    <div>
                      <StepLabel active={isActive}>{step.label}</StepLabel>
                      <StepSubLabel>{step.description}</StepSubLabel>
                    </div>
                  </StepItem>
                  {index < STEP_CONFIG.length - 1 && (
                    <StepDivider completed={index < currentStepIndex} />
                  )}
                </React.Fragment>
              );
            })}
          </StepHeader>

          {currentStep === 'prompts' && (
            <Section>
              <SectionTitle>Prompts Disponíveis</SectionTitle>
              <SectionDescription>
                {complianceType === 'menu' 
                  ? 'Configure seu chat selecionando prompts personalizados para guiar a conversa com a IA. Esta etapa é opcional.'
                  : 'Selecione um ou mais prompts para guiar a análise da IA. Você pode pular esta etapa se preferir usar instruções padrão.'
                }
              </SectionDescription>

              <SelectionHeader>
                <div>
                  <SelectionCount>
                    {pluralize(filteredRules.length, 'prompt disponível', 'prompts disponíveis')}
                  </SelectionCount>
                  <HelperText accent={selectedRules.length > 0}>
                    {selectedRules.length > 0
                      ? pluralize(selectedRules.length, 'prompt selecionado', 'prompts selecionados')
                      : 'Nenhum prompt selecionado (opcional)'}
                  </HelperText>
                </div>
                <SelectionActions>
                  <SearchContainer>
                    <SearchIcon>{FiSearch({ size: 16 })}</SearchIcon>
                    <SearchInput
                      type="search"
                      placeholder="Buscar por nome ou descrição"
                      value={ruleSearchTerm}
                      onChange={(event) => setRuleSearchTerm(event.target.value)}
                      aria-label="Buscar prompt"
                    />
                    {ruleSearchTerm && (
                      <ClearSearchButton type="button" onClick={() => setRuleSearchTerm('')}>
                        Limpar
                      </ClearSearchButton>
                    )}
                  </SearchContainer>
                </SelectionActions>
              </SelectionHeader>

              <QuickRepliesContainer>
                <QuickReply 
                  selected={selectedRules.length === rules.length}
                  onClick={handleSelectAllRules}
                >
                  {FiCheck({ size: 16 })}
                  Selecionar Todos ({rules.length})
                </QuickReply>
                <QuickReply 
                  selected={selectedRules.length === 0}
                  onClick={() => setSelectedRules([])}
                >
                  Nenhum Prompt
                </QuickReply>
              </QuickRepliesContainer>

              {rules.length === 0 ? (
                <EmptyState>
                  {FiFileText({ size: 48 })}
                  <h3>Nenhum prompt encontrado</h3>
                  <p>Você pode continuar sem prompts personalizados.</p>
                </EmptyState>
              ) : filteredRules.length === 0 ? (
                <EmptyFilterState>
                  {FiSearch({ size: 48 })}
                  <h3>Nenhum prompt corresponde à sua busca</h3>
                  <p>Tente outros termos ou limpe o filtro.</p>
                  <ClearSearchButton type="button" onClick={() => setRuleSearchTerm('')}>
                    Limpar filtro
                  </ClearSearchButton>
                </EmptyFilterState>
              ) : (
                <SelectionGrid>
                  {filteredRules.map((rule) => (
                    <SelectionCard 
                      key={rule._id}
                      selected={selectedRules.includes(rule._id)}
                      onClick={() => handleRuleToggle(rule._id)}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedRules.includes(rule._id)}
                      onKeyDown={(event) => handleCardKeyDown(event, () => handleRuleToggle(rule._id))}
                    >
                      <CheckboxContainer>
                        <input 
                          type="checkbox" 
                          checked={selectedRules.includes(rule._id)}
                          onChange={(event) => {
                            event.stopPropagation();
                            handleRuleToggle(rule._id);
                          }}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Selecionar prompt ${rule.name}`}
                        />
                      </CheckboxContainer>
                      <CardIcon>
                        {FiFileText({ size: 24 })}
                      </CardIcon>
                      <CardContent>
                        <CardTitle>{rule.name}</CardTitle>
                        <CardDescription>
                          {rule.description || 'Prompt personalizado para análise'}
                        </CardDescription>
                        <Badge>Prompt</Badge>
                      </CardContent>
                    </SelectionCard>
                  ))}
                </SelectionGrid>
              )}
            </Section>
          )}

          {currentStep === 'folders' && (
            <Section>
              <SectionTitle>Pastas de Arquivos</SectionTitle>
              <SectionDescription>
                {complianceType === 'menu'
                  ? 'Selecione pastas com documentos que deseja disponibilizar para consulta durante a conversa. Esta etapa é opcional.'
                  : 'Selecione pastas para enviar arquivos para análise da IA. Esta etapa é opcional.'
                }
              </SectionDescription>

              <SelectionHeader>
                <div>
                  <SelectionCount>
                    {pluralize(filteredFolders.length, 'pasta disponível', 'pastas disponíveis')}
                  </SelectionCount>
                  <HelperText accent={selectedFolders.length > 0}>
                    {selectedFolders.length > 0
                      ? pluralize(selectedFolders.length, 'pasta selecionada', 'pastas selecionadas')
                      : 'Nenhuma pasta selecionada (opcional)'}
                  </HelperText>
                </div>
                <SelectionActions>
                  <SearchContainer>
                    <SearchIcon>{FiSearch({ size: 16 })}</SearchIcon>
                    <SearchInput
                      type="search"
                      placeholder="Buscar por nome ou descrição"
                      value={folderSearchTerm}
                      onChange={(event) => setFolderSearchTerm(event.target.value)}
                      aria-label="Buscar pasta"
                    />
                    {folderSearchTerm && (
                      <ClearSearchButton type="button" onClick={() => setFolderSearchTerm('')}>
                        Limpar
                      </ClearSearchButton>
                    )}
                  </SearchContainer>
                </SelectionActions>
              </SelectionHeader>

              <QuickRepliesContainer>
                <QuickReply 
                  selected={selectedFolders.length === folders.length}
                  onClick={handleSelectAllFolders}
                >
                  {FiCheck({ size: 16 })}
                  Selecionar Todas ({folders.length})
                </QuickReply>
                <QuickReply 
                  selected={selectedFolders.length === 0}
                  onClick={() => setSelectedFolders([])}
                >
                  Nenhuma Pasta
                </QuickReply>
              </QuickRepliesContainer>

              {folders.length === 0 ? (
                <EmptyState>
                  {FiFolder({ size: 48 })}
                  <h3>Nenhuma pasta encontrada</h3>
                  <p>Você pode continuar sem arquivos adicionais.</p>
                </EmptyState>
              ) : filteredFolders.length === 0 ? (
                <EmptyFilterState>
                  {FiSearch({ size: 48 })}
                  <h3>Nenhuma pasta corresponde à sua busca</h3>
                  <p>Revise o termo ou limpe o filtro.</p>
                  <ClearSearchButton type="button" onClick={() => setFolderSearchTerm('')}>
                    Limpar filtro
                  </ClearSearchButton>
                </EmptyFilterState>
              ) : (
                <SelectionGrid>
                  {filteredFolders.map((folder) => (
                    <SelectionCard 
                      key={folder.id}
                      selected={selectedFolders.includes(folder.id)}
                      onClick={() => handleFolderToggle(folder.id)}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedFolders.includes(folder.id)}
                      onKeyDown={(event) => handleCardKeyDown(event, () => handleFolderToggle(folder.id))}
                    >
                      <CheckboxContainer>
                        <input 
                          type="checkbox" 
                          checked={selectedFolders.includes(folder.id)}
                          onChange={(event) => {
                            event.stopPropagation();
                            handleFolderToggle(folder.id);
                          }}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Selecionar pasta ${folder.name}`}
                        />
                      </CheckboxContainer>
                      <CardIcon>
                        {FiFolder({ size: 24 })}
                      </CardIcon>
                      <CardContent>
                        <CardTitle>{folder.name}</CardTitle>
                        <CardDescription>
                          {folder.fileCount} arquivo{folder.fileCount !== 1 ? 's' : ''} • 
                          {folder.description || 'Pasta de documentos'}
                        </CardDescription>
                        <Badge>{pluralize(folder.fileCount, 'arquivo', 'arquivos')}</Badge>
                      </CardContent>
                    </SelectionCard>
                  ))}
                </SelectionGrid>
              )}
            </Section>
          )}

          {currentStep === 'summary' && (
            <Section>
              <SectionTitle>Resumo da Seleção</SectionTitle>
              <SectionDescription>
                Confirme sua seleção antes de prosseguir para o chat.
              </SectionDescription>

              <SummarySection>
                <SummaryTitle>Prompts Selecionados: {selectedRules.length}</SummaryTitle>
                {selectedRules.length > 0 && (
                  <SummaryList>
                    {rules
                      .filter(rule => selectedRules.includes(rule._id))
                      .map(rule => (
                        <SummaryListItem key={rule._id}>{rule.name}</SummaryListItem>
                      ))
                    }
                  </SummaryList>
                )}
              </SummarySection>

              <SummarySection>
                <SummaryTitle>Pastas Selecionadas: {selectedFolders.length}</SummaryTitle>
                {selectedFolders.length > 0 && (
                  <SummaryList>
                    {folders
                      .filter(folder => selectedFolders.includes(folder.id))
                      .map(folder => (
                        <SummaryListItem key={folder.id}>
                          {folder.name} ({folder.fileCount} arquivo{folder.fileCount !== 1 ? 's' : ''})
                        </SummaryListItem>
                      ))
                    }
                  </SummaryList>
                )}
              </SummarySection>
            </Section>
          )}
        </ModalContent>

        <ActionButtons>
          <ActionHint role="status">{actionHintMessage}</ActionHint>

          {currentStep !== 'prompts' && (
            <CancelButton type="button" onClick={handleBack}>
              Voltar
            </CancelButton>
          )}
          
          {currentStep !== 'summary' && (
            <CancelButton type="button" onClick={handleSkipStep}>
              Pular Etapa
            </CancelButton>
          )}
          
          <ContinueButton type="button" onClick={handleContinue}>
            {currentStep === 'summary' ? (
              <>
                {FiSend({ size: 16 })}
                Iniciar Chat
              </>
            ) : (
              <>
                Continuar
                {FiChevronRight({ size: 16 })}
              </>
            )}
          </ContinueButton>
        </ActionButtons>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ChatSetupModal; 